import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Badge, Table, Alert, ProgressBar } from 'react-bootstrap';
import { Doughnut, Line, Radar, PolarArea, Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  LineElement, 
  PointElement, 
  LineController, 
  DoughnutController, 
  RadarController, 
  PolarAreaController,
  BarElement,
  BarController, 
  CategoryScale, 
  LinearScale, 
  RadialLinearScale, 
  Tooltip, 
  Filler, 
  Legend 
} from 'chart.js';
import { taskService, departmentService } from '../../services/api';
import './Analytics.css';
import TaskDistribution from './TaskDistribution';
import TaskTrend from './TaskTrend';
import axios from 'axios';

// Register required Chart.js components
ChartJS.register(
  ArcElement, 
  LineElement, 
  PointElement, 
  LineController, 
  DoughnutController,
  RadarController,
  PolarAreaController,
  BarElement,
  BarController,
  CategoryScale, 
  LinearScale,
  RadialLinearScale,
  Tooltip, 
  Filler,
  Legend
);

const DepartmentAnalytics = ({ onError }) => {
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentData, setDepartmentData] = useState(null);
  const [departmentTasks, setDepartmentTasks] = useState([]);
  const [timeRange, setTimeRange] = useState('month');
  const [error, setError] = useState(null);
  const [periodSummary, setPeriodSummary] = useState({
    completedTasks: 0,
    totalTasks: 0,
    onTimeTasks: 0,
    lateTasks: 0
  });
  const [analytics, setAnalytics] = useState(null);

  // Load departments on mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Load selected department data
  useEffect(() => {
    if (selectedDepartment) {
      fetchDepartmentDetails();
    }
  }, [selectedDepartment, timeRange]);

  const handleError = (errorMessage) => {
    setError(errorMessage);
    if (onError) onError(errorMessage);
  };

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      
      // Fetch all departments
      const response = await departmentService.getAllDepartments();
      
      if (response && response.data && response.data.success) {
        setDepartments(response.data.data || []);
        
        // Select first department by default
        if (response.data.data.length > 0 && !selectedDepartment) {
          setSelectedDepartment(response.data.data[0].id);
        }
      } else {
        handleError('Failed to load departments list');
      }
    } catch (err) {
      handleError('Failed to load departments. Please check your server connection.');
      console.error('Error fetching departments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get department details using departmentService
      const departmentResponse = await departmentService.getDepartmentById(selectedDepartment);
      
      if (departmentResponse?.data?.success) {
        const departmentData = departmentResponse.data.data;
        
        // Get department tasks
        const tasksResponse = await departmentService.getDepartmentTasks(selectedDepartment);
        const tasksData = tasksResponse?.data?.success ? tasksResponse.data.data : [];
        
        // Get department members
        const membersResponse = await departmentService.getDepartmentMembers(selectedDepartment);
        const membersData = membersResponse?.data?.success ? membersResponse.data.data : [];
        
        // Combine all data
        const combinedData = {
          ...departmentData,
          tasks: tasksData,
          members: membersData,
          employeeCount: membersData.length,
          totalTasks: tasksData.length,
          completedTasks: tasksData.filter(task => task.status === 'completed').length,
          inProgressTasks: tasksData.filter(task => task.status === 'in_progress').length,
          pendingTasks: tasksData.filter(task => task.status === 'todo').length,
          awaitingApprovalTasks: tasksData.filter(task => task.status === 'awaiting_approval').length
        };
        
        setDepartmentData(combinedData);
        setDepartmentTasks(tasksData);
        
        // Set period summary
        setPeriodSummary({
          completedTasks: combinedData.completedTasks,
          totalTasks: combinedData.totalTasks,
          onTimeTasks: tasksData.filter(task => 
            task.status === 'completed' && 
            new Date(task.completed_at) <= new Date(task.due_date)
          ).length,
          lateTasks: tasksData.filter(task => 
            task.status === 'completed' && 
            new Date(task.completed_at) > new Date(task.due_date)
          ).length
        });
        
      } else {
        handleError('No data available for this department.');
      }
    } catch (err) {
      handleError('Failed to load department details. Please try again later.');
      console.error('Error fetching department data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentChange = (deptId) => {
    setSelectedDepartment(Number(deptId));
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  // Generate status distribution data for charts
  const getStatusData = () => {
    const completed = departmentTasks.filter(task => task.status === 'completed').length;
    const inProgress = departmentTasks.filter(task => task.status === 'in_progress').length;
    const pending = departmentTasks.filter(task => task.status === 'todo').length;
    const awaitingApproval = departmentTasks.filter(task => task.status === 'awaiting_approval').length;
    
    return {
      labels: ['Completed', 'In Progress', 'Pending', 'Awaiting Approval'],
      datasets: [
        {
          data: [completed, inProgress, pending, awaitingApproval],
          backgroundColor: [
            'rgba(25, 135, 84, 0.8)',  // Success green
            'rgba(13, 110, 253, 0.8)', // Primary blue
            'rgba(108, 117, 125, 0.8)', // Secondary gray
            'rgba(255, 193, 7, 0.8)'  // Warning yellow
          ],
          borderColor: [
            'rgba(25, 135, 84, 1)',
            'rgba(13, 110, 253, 1)',
            'rgba(108, 117, 125, 1)',
            'rgba(255, 193, 7, 1)'
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  // Generate priority distribution data
  const getPriorityData = () => {
    const high = departmentTasks.filter(task => task.priority === 'high').length;
    const medium = departmentTasks.filter(task => task.priority === 'medium').length;
    const low = departmentTasks.filter(task => task.priority === 'low').length;
    const urgent = departmentTasks.filter(task => task.priority === 'urgent').length;
    
    return {
      labels: ['High', 'Medium', 'Low', 'Urgent'],
      datasets: [
        {
          data: [high, medium, low, urgent],
          backgroundColor: [
            'rgba(255, 193, 7, 0.8)', // Warning yellow
            'rgba(108, 117, 125, 0.8)', // Secondary gray
            'rgba(13, 202, 240, 0.8)', // Info blue
            'rgba(220, 53, 69, 0.8)'  // Danger red
          ],
        },
      ],
    };
  };

  // Generate completion trend data
  const getCompletionTrendData = () => {
    if (departmentData?.monthlyData) {
      return {
        labels: departmentData.monthlyData.map(item => item.period),
        datasets: [
          {
            label: 'Completion Rate',
            data: departmentData.monthlyData.map(item => item.completionRate),
            borderColor: 'rgba(13, 110, 253, 1)',
            backgroundColor: 'rgba(13, 110, 253, 0.1)',
            tension: 0.4,
            fill: true,
          }
        ],
      };
    }
    
    // Return empty data if not available
    return {
      labels: [],
      datasets: [
        {
          label: 'Tasks Completed',
          data: [],
          borderColor: 'rgba(13, 110, 253, 1)',
          backgroundColor: 'rgba(13, 110, 253, 0.1)',
          tension: 0.4,
          fill: true,
        }
      ],
    };
  };

  const fetchDepartmentAnalytics = async () => {
    setLoading(true);
    try {
      const response = await taskService.getDepartmentAnalytics(selectedDepartment, timeRange);
      if (response && response.success) {
        setAnalytics(response.data || {});
        setError(null);
      } else {
        handleError('Failed to fetch analytics data');
      }
    } catch (err) {
      handleError(err.response?.data?.message || 'Failed to fetch department analytics data');
      console.error('Error fetching department analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !departmentData) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading department data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <h1 className="analytics-title mb-4">
        <i className="fas fa-chart-pie text-primary me-2"></i> 
        Department Analytics
      </h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Select Department</Form.Label>
                <Form.Select
                  value={selectedDepartment || ''}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select a department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Time Range</Form.Label>
                <Form.Select
                  value={timeRange}
                  onChange={(e) => handleTimeRangeChange(e.target.value)}
                  disabled={loading}
                >
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading department analytics data...</p>
        </div>
      ) : (
        <>
          {!departmentData ? (
            <Alert variant="info" className="mt-4">
              Please select a department to view analytics data.
            </Alert>
          ) : (
            <>
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="shadow-sm h-100 pulse-card">
                    <Card.Body className="text-center">
                      <div className="stat-icon-container">
                        <i className="fas fa-users stat-icon text-primary"></i>
                      </div>
                      <h2 className="display-4 fw-bold">{departmentData.employeeCount || 0}</h2>
                      <p className="text-muted mb-0">Team Members</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="shadow-sm h-100 pulse-card">
                    <Card.Body className="text-center">
                      <div className="stat-icon-container">
                        <i className="fas fa-tasks stat-icon text-warning"></i>
                      </div>
                      <h2 className="display-4 fw-bold">{departmentData.totalTasks || 0}</h2>
                      <p className="text-muted mb-0">Total Tasks</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="shadow-sm h-100 pulse-card">
                    <Card.Body className="text-center">
                      <div className="stat-icon-container">
                        <i className="fas fa-check-circle stat-icon text-success"></i>
                      </div>
                      <h2 className="display-4 fw-bold">{departmentData.completedTasks || 0}</h2>
                      <p className="text-muted mb-0">Completed Tasks</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="shadow-sm h-100 pulse-card">
                    <Card.Body className="text-center">
                      <div className="stat-icon-container">
                        <i className="fas fa-spinner stat-icon text-info"></i>
                      </div>
                      <h2 className="display-4 fw-bold">{departmentData.inProgressTasks || 0}</h2>
                      <p className="text-muted mb-0">In Progress</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={12}>
                  <TaskTrend 
                    trendData={departmentData?.monthlyData?.map(item => ({
                      date: item.period,
                      completed: item.completedTasks || 0,
                      pending: item.pendingTasks || 0
                    })) || []}
                    title="Task Completion Trend"
                  />
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <TaskDistribution 
                    distributionData={{
                      labels: ['Completed', 'In Progress', 'Pending', 'Awaiting Approval'],
                      data: [
                        departmentTasks.filter(task => task.status === 'completed').length,
                        departmentTasks.filter(task => task.status === 'in_progress').length,
                        departmentTasks.filter(task => task.status === 'todo').length,
                        departmentTasks.filter(task => task.status === 'awaiting_approval').length
                      ],
                      centerText: {
                        value: departmentTasks.length,
                        label: 'Total Tasks'
                      }
                    }}
                    title="Task Status Distribution"
                  />
                </Col>
                <Col md={6}>
                  <Card className="shadow-sm h-100">
                    <Card.Header className="bg-white">
                      <h5 className="mb-0">Task Priority Distribution</h5>
                    </Card.Header>
                    <Card.Body className="d-flex align-items-center justify-content-center">
                      <div style={{ height: '300px', width: '300px' }}>
                        <PolarArea 
                          data={getPriorityData()}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'right',
                              }
                            }
                          }} 
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Card className="shadow-sm">
                <Card.Header className="bg-white">
                  <h5 className="mb-0">Recent Department Tasks</h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Assigned To</th>
                          <th>Status</th>
                          <th>Priority</th>
                          <th>Due Date</th>
                          <th>Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departmentTasks.slice(0, 8).map(task => (
                          <tr key={task.id}>
                            <td>
                              <div className="fw-bold">{task.title}</div>
                              <div className="small text-muted">
                                {task.description 
                                  ? (task.description.length > 40 
                                      ? `${task.description.substring(0, 40)}...` 
                                      : task.description) 
                                  : 'No description'}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {task.assigned_to_name || 'Unassigned'}
                              </div>
                            </td>
                            <td>
                              <Badge bg={
                                task.status === 'completed' ? 'success' :
                                task.status === 'in_progress' ? 'primary' :
                                task.status === 'awaiting_approval' ? 'warning' :
                                'secondary'
                              }>
                                {task.status?.replace('_', ' ') || 'Unknown'}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={
                                task.priority === 'high' ? 'danger' :
                                task.priority === 'medium' ? 'warning' :
                                'info'
                              }>
                                {task.priority || 'Normal'}
                              </Badge>
                            </td>
                            <td>
                              {task.due_date 
                                ? new Date(task.due_date).toLocaleDateString() 
                                : 'No deadline'}
                            </td>
                            <td style={{ width: '15%' }}>
                              <ProgressBar 
                                now={task.status === 'completed' ? 100 : task.status === 'in_progress' ? 50 : 0} 
                                variant={
                                  task.status === 'completed' ? 'success' : 
                                  task.status === 'in_progress' ? 'primary' : 
                                  'secondary'
                                } 
                                style={{ height: '8px' }} 
                              />
                            </td>
                          </tr>
                        ))}
                        {departmentTasks.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <i className="fas fa-clipboard me-2"></i>
                              No tasks in this department
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default DepartmentAnalytics; 